"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InAppAgentRunRequestSchema = exports.InAppAgentRunErrorCode = exports.InAppAgentRunStatusSchema = exports.InAppAgentRunStatus = void 0;
const zod_1 = __importDefault(require("zod"));
const schema_1 = require("../../in-app-agent/schema");
/**
 * Lifecycle states of an in-app agent run (`in_app_agent_runs.status`).
 *
 * Stored as a plain string column (not a PG enum) so states can be added
 * without `ALTER TYPE`, following BatchExportStatus/BatchActionStatus.
 * Legacy rows may still be null, while every current writer sets it
 * explicitly. Readers therefore keep handling a missing status.
 */
var InAppAgentRunStatus;
(function (InAppAgentRunStatus) {
    InAppAgentRunStatus["QUEUED"] = "QUEUED";
    InAppAgentRunStatus["RUNNING"] = "RUNNING";
    InAppAgentRunStatus["AWAITING_APPROVAL"] = "AWAITING_APPROVAL";
    InAppAgentRunStatus["SUCCEEDED"] = "SUCCEEDED";
    InAppAgentRunStatus["FAILED"] = "FAILED";
    InAppAgentRunStatus["CANCELLED"] = "CANCELLED";
})(InAppAgentRunStatus || (exports.InAppAgentRunStatus = InAppAgentRunStatus = {}));
exports.InAppAgentRunStatusSchema = zod_1.default.enum(InAppAgentRunStatus);
/**
 * Terminal error codes of an in-app agent run (`in_app_agent_runs.error_code`).
 *
 * Constrains writers at the type level; the column itself stays a free-form
 * string and historical rows may contain other values, so readers must
 * tolerate unknown strings rather than parse against this enum.
 */
var InAppAgentRunErrorCode;
(function (InAppAgentRunErrorCode) {
    /** Unfinished run lazily closed because its foreground stream died. */
    InAppAgentRunErrorCode["STALE"] = "stale";
    /** Client aborted the request. */
    InAppAgentRunErrorCode["CANCELLED"] = "cancelled";
    /** Agent loop failed while streaming. */
    InAppAgentRunErrorCode["AGENT_ERROR"] = "agent_error";
    /** Agent initialization failed before the loop started. */
    InAppAgentRunErrorCode["INIT_FAILED"] = "init_failed";
    /** Background worker aborted the run on SIGTERM (deploy). */
    InAppAgentRunErrorCode["WORKER_SHUTDOWN"] = "worker_shutdown";
    /**
     * Background run died after an approved mutation may have started but
     * before its result was persisted. Never generically retried: the next
     * turn must verify whether the effect landed before proposing again.
     */
    InAppAgentRunErrorCode["OUTCOME_UNKNOWN"] = "outcome_unknown";
    /** Run committed as QUEUED but the BullMQ enqueue failed right after. */
    InAppAgentRunErrorCode["ENQUEUE_FAILED"] = "enqueue_failed";
    /** QUEUED past `QUEUE_TIMEOUT`: no worker ever picked the run up. */
    InAppAgentRunErrorCode["QUEUE_TIMEOUT"] = "queue_timeout";
    /** RUNNING with a heartbeat older than `HEARTBEAT_STALE`: worker died. */
    InAppAgentRunErrorCode["WORKER_LOST"] = "worker_lost";
    /**
     * RUNNING for longer than `RUN_MAX_DURATION` since claim — the backstop
     * against a hung tool renewing an otherwise healthy heartbeat forever.
     */
    InAppAgentRunErrorCode["RUN_TIMEOUT"] = "run_timeout";
    /** Approval parked longer than `APPROVAL_TTL` without a decision. */
    InAppAgentRunErrorCode["APPROVAL_EXPIRED"] = "approval_expired";
    /** Pending approval replaced by a newer user message (recorded on CANCELLED). */
    InAppAgentRunErrorCode["APPROVAL_SUPERSEDED"] = "approval_superseded";
    /** Pending approval cancelled by the user (recorded on CANCELLED). */
    InAppAgentRunErrorCode["APPROVAL_CANCELLED"] = "approval_cancelled";
})(InAppAgentRunErrorCode || (exports.InAppAgentRunErrorCode = InAppAgentRunErrorCode = {}));
/**
 * Typed contents of `in_app_agent_runs.request` — the server-side trigger
 * payload a background worker reads at claim. The BullMQ job carries only
 * `{projectId, runId}`, so everything the run needs that is not already in
 * the append-only event stream lives here. The user message itself is not
 * duplicated: the submitting mutation appends it as a conversation event
 * and the worker rebuilds model input from the event history.
 */
exports.InAppAgentRunRequestSchema = zod_1.default.discriminatedUnion("kind", [
    zod_1.default.object({
        kind: zod_1.default.literal("userMessage"),
        /** Sanitized AG-UI context items (current page, resolved view filters). */
        context: zod_1.default.array(schema_1.AgUiContextSchema),
    }),
    zod_1.default.object({
        kind: zod_1.default.literal("approvalDecision"),
        /** Provenance for debugging only; no code path queries lineage. */
        parentRunId: zod_1.default.string(),
        toolCallId: zod_1.default.string(),
        approved: zod_1.default.boolean(),
        /** Inherited sanitized context; defaults for legacy continuation rows. */
        context: zod_1.default.array(schema_1.AgUiContextSchema).default([]),
    }),
]);
